import React from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { EventOSData } from "../../../types";
import { TabId, WorkspaceArea } from "../types";
import { DataTable } from "./DataTable";
import { ActivityAnalyticsPanel } from "./ActivityAnalyticsPanel";
import { LiveGuestListPanel } from "./LiveGuestListPanel";
import { LiveTicketEntriesPanel } from "./LiveTicketEntriesPanel";
import { MetricGrid } from "./MetricGrid";
import { formatINR, formatINRCompact } from "../../../utils/format";
import { CHART_BAR_CURSOR, chartTooltipProps } from "../../../constants/chartTheme";

/* html5-qrcode is ~130kB gzipped and only this panel needs it. Loading it
   lazily keeps it out of the main bundle, which matters most for the mobile
   users who reach this screen over a venue's network. */
const TicketScannerPanel = React.lazy(() =>
  import("./TicketScannerPanel").then((m) => ({ default: m.TicketScannerPanel }))
);

const CHART_AXIS_TICK = { fill: "#b2b3c1", fontSize: 12 };

function trimChartLabel(label: string): string {
  if (label.length <= 12) return label;
  return `${label.slice(0, 10)}…`;
}

function formatCompactCount(value: number): string {
  if (!Number.isFinite(value)) return "0";
  return new Intl.NumberFormat("en-IN", { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

type Props = {
  tab: TabId;
  data: EventOSData;
  area: WorkspaceArea;
  eventId?: string | null;
  token?: string | null;
  embedded?: boolean;
};

export const EventOSTabContent: React.FC<Props> = ({ tab, data, area, eventId, token, embedded = false }) => {
  switch (tab) {
    case "overview": {
      if (area === "event-os") {
        return (
          <>
            <MetricGrid metrics={data.liveMetrics} cols={5} />
            <div className="eventos-card">
              <div className="eventos-label">Live Operations Snapshot</div>
              <div className="eventos-subtle">Headcount, staffing readiness, and command-center signals for current execution.</div>
            </div>
            <div className="eventos-card">
              <div className="eventos-label">Staff Deployment</div>
              <DataTable table={data.workforceTable} emptyLabel="Workforce deployment rows will appear from staffing endpoints." />
            </div>
          </>
        );
      }

      const revenueSeries = data.revenueChart.map((entry) => ({ ...entry, value: Number(entry.value) || 0 }));
      const expenseSeries = data.expenseChart.map((entry) => ({ ...entry, value: Number(entry.value) || 0 }));
      return (
        <>
          <MetricGrid metrics={data.overviewMetrics} cols={5} />
          <div className="eventos-grid cols-2">
            <div className="eventos-card">
              <div className="eventos-label">Revenue Profit/Loss</div>
              {revenueSeries.length === 0 ? (
                <div className="eventos-empty">Revenue chart data pending API connection.</div>
              ) : (
                <div className="eventos-chart-wrap">
                  <ResponsiveContainer>
                    <BarChart data={revenueSeries} margin={{ top: 16, right: 8, left: 0, bottom: 14 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                      <XAxis dataKey="name" tickFormatter={trimChartLabel} tick={CHART_AXIS_TICK} interval={0} />
                      <YAxis tickFormatter={formatINRCompact} tick={CHART_AXIS_TICK} width={70} />
                      <ReferenceLine y={0} stroke="rgba(255,255,255,0.28)" strokeDasharray="4 4" />
                      <Tooltip
                        {...chartTooltipProps}
                        cursor={CHART_BAR_CURSOR}
                        formatter={(value: number | string, name: string) => [formatINR(Number(value) || 0), name]}
                        labelFormatter={(label) => `Tier: ${label}`}
                      />
                      <Bar dataKey="value" name="P/L">
                        {revenueSeries.map((entry) => (
                          <Cell key={`${entry.name}-pl`} fill={Number(entry.value) >= 0 ? "#22C55E" : "#E50914"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            <div className="eventos-card">
              <div className="eventos-label">Cost Breakdown</div>
              {expenseSeries.length === 0 ? (
                <div className="eventos-empty">Cost breakdown data pending API connection.</div>
              ) : (
                <div className="eventos-chart-wrap">
                  <ResponsiveContainer>
                    <AreaChart data={expenseSeries} margin={{ top: 16, right: 10, left: 0, bottom: 14 }}>
                      <defs>
                        <linearGradient id="expenseFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#E50914" stopOpacity={0.38} />
                          <stop offset="95%" stopColor="#E50914" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                      <XAxis dataKey="name" tickFormatter={trimChartLabel} tick={CHART_AXIS_TICK} interval={0} />
                      <YAxis tickFormatter={formatINRCompact} tick={CHART_AXIS_TICK} width={70} />
                      <Tooltip
                        {...chartTooltipProps}
                        formatter={(value: number | string, name: string) => [formatINR(Number(value) || 0), name]}
                        labelFormatter={(label) => `Category: ${label}`}
                      />
                      <Area type="monotone" dataKey="value" stroke="#E50914" fill="url(#expenseFill)" strokeWidth={2.5} />
                      <Line type="monotone" dataKey="value" stroke="#FF6B6F" strokeWidth={2} dot={{ r: 3.5 }} activeDot={{ r: 5 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        </>
      );
    }
    case "pnl":
      return <MetricGrid metrics={data.pnlMetrics} cols={4} />;
    case "tickets": {
      const tierSeries = data.ticketTierChart.map((entry) => ({ ...entry, value: Number(entry.value) || 0 }));
      const tierBarColors = ["#22C55E", "#06B6D4", "#A855F7", "#F59E0B", "#E50914", "#38bdf8"];
      return (
        <>
          <MetricGrid metrics={data.ticketMetrics} cols={4} />
          <div className="eventos-card">
            <div className="eventos-label">Tickets sold by tier</div>
            {tierSeries.length === 0 ? (
              <div className="eventos-empty">Tier sales will appear when ticket data is available.</div>
            ) : (
              <div className="eventos-chart-wrap">
                <ResponsiveContainer>
                  <BarChart data={tierSeries} margin={{ top: 16, right: 10, left: 0, bottom: 14 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="name" tickFormatter={trimChartLabel} tick={CHART_AXIS_TICK} interval={0} />
                    <YAxis tickFormatter={formatCompactCount} tick={CHART_AXIS_TICK} width={56} allowDecimals={false} />
                    <Tooltip
                      {...chartTooltipProps}
                      cursor={CHART_BAR_CURSOR}
                      formatter={(value: number | string) => [`${Number(value).toLocaleString("en-IN")} sold`, "Tickets"]}
                      labelFormatter={(label) => `Tier: ${label}`}
                    />
                    <Bar dataKey="value" name="Sold" radius={[8, 8, 0, 0]}>
                      {tierSeries.map((entry, idx) => (
                        <Cell key={`${entry.name}-sold`} fill={tierBarColors[idx % tierBarColors.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          <div className="eventos-card">
            <div className="eventos-label">Ticket Tier Breakdown</div>
            <DataTable table={data.ticketTable} emptyLabel="Ticket tier rows will appear from ticket intelligence endpoint." />
          </div>
          {eventId && token ? <ActivityAnalyticsPanel eventId={eventId} token={token} /> : null}
        </>
      );
    }
    case "team":
      return (
        <>
          <MetricGrid metrics={data.workforceMetrics} cols={4} />
          <div className="eventos-card">
            <div className="eventos-label">Staff Deployment</div>
            <DataTable table={data.workforceTable} emptyLabel="Workforce data will populate from staffing endpoints." />
          </div>
        </>
      );
    case "risk":
      return (
        <>
          <MetricGrid metrics={data.riskMetrics} cols={4} />
          <div className="eventos-card">
            <div className="eventos-label">Risk Register</div>
            <DataTable table={data.riskTable} emptyLabel="Risk register will load from compliance endpoints." />
          </div>
        </>
      );
    case "live":
      return (
        <div className="live-ops-workspace">
          {!embedded ? <MetricGrid metrics={data.liveMetrics} cols={5} /> : null}
          {eventId && token ? (
            <div className="live-ops-workspace__panels">
              <React.Suspense fallback={<div className="eventos-card eventos-subtle">Loading scanner…</div>}>
                <TicketScannerPanel eventId={eventId} token={token} />
              </React.Suspense>
              <LiveTicketEntriesPanel eventId={eventId} token={token} tiers={data.ticketTiers} />
              <LiveGuestListPanel eventId={eventId} token={token} />
            </div>
          ) : (
            <div className="eventos-card">
              <div className="eventos-empty">Sign in and select an event to use live operations.</div>
            </div>
          )}
        </div>
      );
    default:
      return null;
  }
};
